/**
 * Web-layer platform detection — Apple/macOS native alignment.
 *
 * Complements lib/nativeApp.ts (which tags the Capacitor iOS/Android shell
 * with `capacitor-native platform-ios|android`): this module tags the
 * browser/PWA experience with the HOST OS so CSS and components can follow
 * platform conventions — San Francisco type, native overlay scrollbars,
 * ⌘-based shortcut hints, and trackpad gesture guards on macOS.
 *
 * Classes added to <html> by initPlatform():
 *   platform-apple       any Apple OS (macOS, iOS, iPadOS)
 *   platform-macos       desktop macOS specifically
 *   platform-standalone  running as an installed app window
 *                        (macOS Dock / iOS Home Screen / desktop PWA)
 *
 * Call initPlatform() once from main.tsx before first render so the classes
 * exist at first paint.
 */

import { isStandalone } from './pwa';

/** `navigator.userAgentData` is Chromium-only and missing from lib.dom. */
interface NavigatorWithUAData extends Navigator {
  userAgentData?: { platform?: string };
}

function platformString(): string {
  if (typeof navigator === 'undefined') return '';
  const nav = navigator as NavigatorWithUAData;
  return nav.userAgentData?.platform ?? nav.platform ?? '';
}

/**
 * Desktop macOS. iPadOS 13+ reports "MacIntel" but exposes multi-touch,
 * so it is excluded here and counted as iOS instead.
 */
export function isMacOS(): boolean {
  if (!/mac/i.test(platformString())) return false;
  return (navigator.maxTouchPoints ?? 0) <= 1;
}

/** iPhone / iPad / iPod, including iPadOS 13+ masquerading as Mac. */
export function isIOS(): boolean {
  const p = platformString();
  if (/iphone|ipad|ipod/i.test(p)) return true;
  return /mac/i.test(p) && (navigator.maxTouchPoints ?? 0) > 1;
}

/** Any Apple OS — the platforms whose conventions (⌘, SF type) apply. */
export function isApplePlatform(): boolean {
  return isMacOS() || isIOS();
}

/** Primary shortcut modifier label: ⌘ on Apple platforms, Ctrl elsewhere. */
export function modKeyLabel(): '⌘' | 'Ctrl' {
  return isApplePlatform() ? '⌘' : 'Ctrl';
}

/** Platform-native shortcut hint: "K" → "⌘K" on Apple, "Ctrl+K" elsewhere. */
export function shortcutLabel(key: string): string {
  return isApplePlatform() ? `⌘${key}` : `Ctrl+${key}`;
}

/**
 * True when the platform's PRIMARY shortcut modifier is pressed: ⌘ on Apple,
 * Ctrl elsewhere. On Apple, plain Ctrl (and ⌘+Ctrl chords) return false so
 * macOS's native Ctrl text-editing keys — Ctrl+K kill-line, Ctrl+A/E line
 * jumps — keep working inside inputs instead of being hijacked as app
 * shortcuts.
 */
export function isPrimaryModPressed(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>): boolean {
  return isApplePlatform() ? e.metaKey && !e.ctrlKey : e.ctrlKey;
}

export function initPlatform(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (isApplePlatform()) root.classList.add('platform-apple');
  if (isMacOS()) root.classList.add('platform-macos');

  const applyStandalone = (standalone: boolean): void => {
    root.classList.toggle('platform-standalone', standalone);
  };
  applyStandalone(isStandalone());

  // Follow live display-mode changes (e.g. tab handed off to the installed
  // app window). Older WebKit only ships addListener, hence the guard.
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(display-mode: standalone)');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', (ev) => applyStandalone(ev.matches));
    }
  }
}
